import { useEffect, useRef, useState } from 'react'
import { Sheet } from '../../ui/Sheet'
import { Icon } from '../../ui/Icon'
import type { Bookmark } from '../../transport/api'
import { clockLabel } from '../blockTime'

/**
 * THE BOOKMARKS LIST — owner, 2026-10-31: 在右側欄位呢, 會出現一個可以打開的小抽屜, 就像 Note 一樣, 它可以使用另外一種顏色來
 * 表示 … 跳出來書籤的瀏覽 list. 當使用者點向某一個 List 的時候呢, 就可以跑到該直接跳到該 Message Block … 寬螢幕的時候呢,
 * 這個書籤的收藏頁就放到跟原本 Note 的那一個地方一樣的地方.
 *
 * The same tall sheet the notes open in, in the bookmarks' own colour: the agent's kept messages, the newest kept first,
 * each with who said it, when, and its first lines — enough to recognise it before going there. A row goes to its
 * message; one from an earlier conversation of this agent cannot be gone to, so pressing it opens its whole text here
 * instead. Each can be removed.
 */
/**
 * The first lines of a message as one run of words: its line breaks, list marks and emphasis marks would each spend a
 * line of the three the row shows (a reply's blank line left a lone "…" on app-dev). Opened, a row shows the words whole.
 */
export function gist(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, ' [code] ')
    .replace(/^\s{0,3}(#{1,6}\s+|[-*+]\s+|\d+[.)]\s+|>\s?)/gm, '')
    .replace(/(\*\*|__|`)/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function BookmarkPanel({
  agentName,
  list,
  loading,
  error,
  currentFile,
  onGo,
  onRemove,
  onClose,
  onOpen,
  errorText = 'The bookmarks could not be read from this agent’s machine. Try again in a moment.',
}: {
  agentName: string
  list: Bookmark[]
  loading: boolean
  error: unknown
  /** The transcript this agent's Clean View is showing — a bookmark from another file is from an earlier conversation. */
  currentFile: string | null
  onGo: (b: Bookmark) => void
  onRemove: (id: string) => void
  onClose: () => void
  /** Read the list again — another device may have kept something since. */
  onOpen: () => void
  /** What a failed read says — where the list is kept (an agent's machine, a project's chat service). */
  errorText?: string
}) {
  // Once, when the sheet opens.
  const reread = useRef(onOpen)
  useEffect(() => reread.current(), [])
  return (
    <Sheet
      title="Bookmarks"
      head={
        <span className="bm-head">
          <Icon name="bookmarks" size={18} />
          <span>Bookmarks</span>
          <span className="bm-head-who">{agentName}</span>
        </span>
      }
      onClose={onClose}
      className="sheet-tall bm-sheet"
    >
      {loading && !list.length && <div className="bm-empty">Loading bookmarks…</div>}
      {!!error && !list.length && <div className="bm-empty bm-err">{errorText}</div>}
      {!loading && !error && !list.length && (
        <div className="bm-empty">
          <Icon name="bookmark" size={22} />
          <span>No bookmarks yet. Tap the bookmark under a message to keep it here.</span>
        </div>
      )}
      <BookmarkRows list={list} currentFile={currentFile} label={`Bookmarks of ${agentName}`} onGo={onGo} onRemove={onRemove} />
    </Sheet>
  )
}

/**
 * The kept messages as rows — the drawer's list, and each group of More › Bookmarks (shell/BookmarksPage). A row goes to
 * its message; one from an earlier conversation of its agent cannot be gone to, so pressing it opens its whole text in
 * place instead. Each can be removed.
 */
export function BookmarkRows({
  list,
  currentFile,
  label,
  onGo,
  onRemove,
}: {
  list: Bookmark[]
  /** The transcript the agent's Clean View is showing; a bookmark from another file is from an earlier conversation. */
  currentFile: string | null
  label: string
  onGo: (b: Bookmark) => void
  onRemove: (id: string) => void
}) {
  const [open, setOpen] = useState<string | null>(null)
  return (
    <ul className="bm-list" aria-label={label}>
      {list.map((b) => {
        const earlier = !!currentFile && !!b.file && b.file !== currentFile
        const expanded = open === b.id
        return (
          <li key={b.id} className={`bm-item bm-${b.kind}${earlier ? ' bm-earlier' : ''}`}>
            <button
              type="button"
              className="bm-go"
              aria-expanded={earlier ? expanded : undefined}
              title={earlier ? 'From an earlier conversation — show all of it' : 'Go to this message'}
              onClick={() => {
                if (earlier) setOpen(expanded ? null : b.id)
                else onGo(b)
              }}
            >
              <span className="bm-meta">
                <span className="bm-who">{b.who}</span>
                {b.ts !== null && <span className="bm-when">{clockLabel(b.ts)}</span>}
                {earlier && <span className="bm-tag">earlier conversation</span>}
              </span>
              <span className={`bm-text${expanded ? ' all' : ''}`}>{expanded ? b.text : gist(b.text)}</span>
            </button>
            <button
              type="button"
              className="bm-x"
              aria-label={`Remove the bookmark: ${b.text.slice(0, 40)}`}
              title="Remove this bookmark"
              disabled={b.id.startsWith('pending:')}
              onClick={() => onRemove(b.id)}
            >
              <Icon name="close" size={15} />
            </button>
          </li>
        )
      })}
    </ul>
  )
}
